import { DeleteOutlined, EditOutlined, MoreHorizOutlined, PauseOutlined } from '@mui/icons-material';
import { Menu, Button, ListItemIcon, ListItemText, MenuItem, PopoverOrigin, Stack, Tooltip } from '@mui/material';
import { Permission } from '@procureai/permissions';
import { usePermissions } from '@procureai/permissions-ui';
import { useButtonMenu, useConfirmationDialog, WrapWith } from '@procureai/ui-mui';
import { useMemo } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { useRuleActions } from '../../hooks/useRuleActions/useRuleActions';
import { useRuleDetailDialogControls } from '../../hooks/useRuleDetailDialogControls/useRuleDetailDialogControls';
import { RuleDetailPageAction } from '../../RuleDetail.types';
import { RuleDetailDialog } from '../RuleDetailDialog/RuleDetailDialog';
import { useRuleVersionActionsContext } from '../RuleVersionActionsProvider/RuleVersionActionsProvider.context';
import { I18nNamespace } from '~/common/types';
import { RouteId } from '~/routes/RouteId';

const ANCHOR_ORIGIN: PopoverOrigin = {
  vertical: 'bottom',
  horizontal: 'left',
};
const TRANSFORM_ORIGIN: PopoverOrigin = {
  vertical: 'top',
  horizontal: 'left',
};
const MAPPED_ICONS = {
  [RuleDetailPageAction.EDIT_SETTINGS]: <EditOutlined fontSize="small" />,
  [RuleDetailPageAction.DELETE]: <DeleteOutlined fontSize="small" />,
  [RuleDetailPageAction.PAUSE]: <PauseOutlined fontSize="small" />,
};

interface RulePageActionsProps {
  id: string;
}

interface MenuItemProps {
  disabled: boolean;
  onClick: () => void;
}

export const RulePageActions = ({ id }: RulePageActionsProps) => {
  const { t: tAction } = useTranslation(I18nNamespace.ruleBuilder, { keyPrefix: 'DetailPage.action' });
  const { t: tMessage } = useTranslation(I18nNamespace.ruleBuilder, { keyPrefix: 'DetailPage.message' });
  const navigate = useNavigate();
  // dialog uses its own useRuleActions for mutating details, so just using controls for opening it
  const dialogControls = useRuleDetailDialogControls();
  // omitting save because this is called as onSubmit on RuleBuilder itself.
  // Leveraging just the API hook to trigger submit
  const {
    save,
    publish,
    pause,
    loading: isUpdating,
    isPauseable,
    isPublishable,
    isRemovable,
  } = useRuleVersionActionsContext();

  const { remove, loading: isDeleting } = useRuleActions();
  const { confirm } = useConfirmationDialog();
  const { hasPermission } = usePermissions();
  const { menuProps, buttonProps } = useButtonMenu();
  const actions = [RuleDetailPageAction.EDIT_SETTINGS, RuleDetailPageAction.DELETE, RuleDetailPageAction.PAUSE];

  const actionProps: Record<RuleDetailPageAction, MenuItemProps> = useMemo(
    () => ({
      [RuleDetailPageAction.EDIT_SETTINGS]: {
        onClick: dialogControls.openDialog,
        disabled: false,
      },
      [RuleDetailPageAction.DELETE]: {
        onClick: confirm(
          async () => {
            await remove({ id });
            navigate(RouteId.ruleList);
          },
          {
            title: tAction(RuleDetailPageAction.DELETE),
            message: <Trans t={tMessage} i18nKey={RuleDetailPageAction.DELETE} />,
          },
        ),
        disabled: !isRemovable,
      },
      [RuleDetailPageAction.PAUSE]: {
        onClick: confirm(
          async () => {
            await pause();
          },
          {
            title: tAction(RuleDetailPageAction.PAUSE),
            message: <Trans t={tMessage} i18nKey={RuleDetailPageAction.PAUSE} />,
          },
        ),
        disabled: !isPauseable,
      },
    }),
    [dialogControls.openDialog, confirm, tAction, tMessage, isRemovable, isPauseable, remove, id, navigate, pause],
  );

  const isProcessing = isUpdating || isDeleting;

  return (
    <>
      <Stack
        spacing={3}
        direction="row"
        sx={{
          overflow: 'hidden',
          alignItems: 'center',
        }}
      >
        <Button variant="outlined" color="primary" {...buttonProps} sx={{ minHeight: '40px' }}>
          <MoreHorizOutlined />
        </Button>
        <Menu anchorOrigin={ANCHOR_ORIGIN} transformOrigin={TRANSFORM_ORIGIN} {...menuProps}>
          <WrapWith component={Tooltip} title={tAction(`remove.noPermission`)} placement="left">
            <div>
              {actions.map((action) => (
                <MenuItem key={action} {...actionProps[action]}>
                  <ListItemIcon>{MAPPED_ICONS[action]}</ListItemIcon>
                  <ListItemText primary={tAction(action)} />
                </MenuItem>
              ))}
            </div>
          </WrapWith>
        </Menu>
        <Button
          variant="outlined"
          color="primary"
          onClick={publish}
          disabled={!hasPermission(Permission.RuleEdit) || isProcessing || !isPublishable}
        >
          {tAction('publish')}
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={save}
          disabled={!hasPermission(Permission.RuleEdit) || isProcessing}
        >
          {tAction('save')}
        </Button>
      </Stack>
      <RuleDetailDialog {...dialogControls} />
    </>
  );
};
